import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * PD-16713 — what a malware scan said about a stored file: `clean`, `infected`, or
 * `unscannable` (the scanner could not read it). Null means no scan result has been read,
 * which is the case for every file until a scanner (Amazon GuardDuty for S3) is switched on
 * for the bucket; Workd reads its verdict from the object's tag when a file is opened.
 */
export type FormSubmissionFileScanStatus = 'clean' | 'infected' | 'unscannable';

/**
 * PD-16713 — a file a visitor attached to a form's file question.
 *
 * The page uploads each file on its own as it is picked, before the form is sent, so a
 * row starts out UNCLAIMED: it names the form, the question, the form fill
 * (`clientSubmissionId`, the id the page later submits with) and the page's own id for the
 * upload. Submitting the form claims the rows its answer names, in the same transaction
 * that stores the submission (`fk_form_submission__id`). A row nothing claims within
 * FORM_UPLOAD_UNCLAIMED_HOURS (a form never sent, a file removed before sending, a question
 * its Show only when rule hid) is deleted with its file by the nightly forms job, and a
 * claimed one is deleted with its submission under the submission's retention.
 *
 * The bytes are a `_file` row in the tenant's private file storage (FilesService
 * .uploadDirect, `_type` `form`). A picture is stored as Workd's re-encoded copy, never the
 * visitor's original; `contentType` is what the content was found to be, not what the
 * browser said. The (form, fill, upload) key is unique, so an upload the page retries
 * after losing the answer is stored once.
 *
 * SM1 exposure: none. A table the earlier CRM never names.
 */
@Index(
	'UK_form_submission_file_upload',
	['fkFormId', 'clientSubmissionId', 'clientUploadId'],
	{ unique: true },
)
@Index('idx_form_submission_file_submission', ['fkFormSubmissionId'], {})
@Index(
	'idx_form_submission_file_unclaimed',
	['fkFormSubmissionId', 'createdDate'],
	{},
)
@Index('idx_form_submission_file_file', ['fkFileId'], {})
@Entity('form_submission_file')
export class FormSubmissionFileEntity extends BaseEntity {
	@Column('int', { name: 'fk_form__id', unsigned: true })
	public fkFormId!: number;

	// The file question it was uploaded for.
	@Column('varchar', { name: 'fieldKey', length: 60 })
	public fieldKey!: string;

	// The form fill: the submission id the page makes once per filled-in form.
	@Column('char', { name: 'clientSubmissionId', length: 36 })
	public clientSubmissionId!: string;

	// The page's id for this upload, which the question's answer names.
	@Column('char', { name: 'clientUploadId', length: 36 })
	public clientUploadId!: string;

	@Column('int', { name: 'fk__file__id', unsigned: true })
	public fkFileId!: number;

	// Null until a submission claims the upload.
	@Column('int', { name: 'fk_form_submission__id', nullable: true, unsigned: true })
	public fkFormSubmissionId!: number | null;

	// The cleaned name staff see (cleanFormFileName), also the stored answer.
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('int', { name: 'sizeBytes', unsigned: true })
	public sizeBytes!: number;

	@Column('varchar', { name: 'contentType', length: 100 })
	public contentType!: string;

	@Column('varchar', { name: 'scanStatus', nullable: true, length: 20 })
	public scanStatus!: FormSubmissionFileScanStatus | null;
}
